import { type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  BadgeDollarSign,
  BarChart3,
  BellRing,
  ChevronLeft,
  ChevronRight,
  CirclePower,
  Eye,
  EyeOff,
  Gamepad2,
  LayoutDashboard,
  LogOut,
  Menu,
  PencilLine,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  TicketCheck,
  Users,
  X,
} from "lucide-react";
import { CaptchaField, useCaptcha } from "../components/CaptchaField";
import { api, ApiError } from "../api";
import type { Game, Session, SiteSettings, Store, Transaction, User } from "../types";
import { Brand, Button, EmptyState, Field, Modal, SelectField, Spinner, StatusBadge, TextAreaField, useToast } from "../components/ui";
import { MessageManagement, SupportManagement } from "./MessagingAdmin";
import { AdminRewards } from "../components/AdminRewards";
import { AdminWallet } from "../components/AdminWallet";
import { AdminBetSync } from "../components/AdminBetSync";
import { FinanceManagement, RiskManagement } from "../components/AdminOperations";
import { AdminPayments } from "../components/AdminPayments";
import { AdminCatalog } from "../components/AdminCatalog";
import { AdminProviderDiagnostics } from "../components/AdminProviderDiagnostics";
import { AdminReferrals } from "../components/AdminReferrals";
import { AdminBalanceGrants } from "../components/AdminBalanceGrants";
import { AdminQaLoginPermits } from "../components/AdminQaLoginPermits";
import { AdminPromotions } from "../components/AdminPromotions";

type View = "balance-grants" | "qa-permits" | "referrals" | "catalog" | "payments" | "risk" | "dashboard" | "games" | "promotions" | "finance" | "members" | "content" | "integration" | "support" | "messages" | "rewards";
type AdminData = Store & { stats: { users: number; activeUsers: number; pendingTransactions: number; enabledGames: number; deposits: number; withdrawals: number } };
const money = new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" });
const dateTime = new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
const pageSize = 10;

const navItems: { view: View; label: string; Icon: typeof LayoutDashboard }[] = [
  { view: "referrals", label: "合营管理", Icon: Users },
  { view: "catalog", label: "接口分类与游戏", Icon: Gamepad2 },
  { view: "balance-grants", label: "内测余额发放", Icon: BadgeDollarSign },
  { view: "payments", label: "支付 / 提现渠道", Icon: BadgeDollarSign },
  { view: "qa-permits", label: "临时测试许可", Icon: ShieldCheck },
  { view: "risk", label: "IP 与重复检测", Icon: ShieldCheck },
  { view: "dashboard", label: "仪表盘", Icon: LayoutDashboard },
  { view: "games", label: "首页游戏配置", Icon: Gamepad2 },
  { view: "promotions", label: "活动管理", Icon: TicketCheck },
  { view: "finance", label: "财务管理", Icon: BadgeDollarSign },
  { view: "rewards", label: "返水与 VIP", Icon: Sparkles },
  { view: "members", label: "会员管理", Icon: Users },
  { view: "support", label: "客服管理", Icon: Users },
  { view: "messages", label: "消息管理", Icon: BellRing },
  { view: "content", label: "内容管理", Icon: Settings2 },
  { view: "integration", label: "接口中心", Icon: Activity },
];

export function AdminApp() {
  const toast = useToast();
  const [session, setSession] = useState<Session | null>(null);
  const [data, setData] = useState<AdminData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mobileNav, setMobileNav] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const [refreshError, setRefreshError] = useState("");
  const searchParams = new URLSearchParams(window.location.search);
  const rawView = searchParams.get("view") as View | null;
  const view: View = navItems.some((item) => item.view === rawView) ? rawView! : "dashboard";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const auth = await api<Session>("/api/auth/session");
      setSession(auth);
      if (auth.authenticated && auth.user?.role === "admin") setData(await api<AdminData>("/api/admin/store"));
      else setData(null);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "后台暂时无法连接。" );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  const refresh = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    setRefreshError("");
    try {
      setData(await api<AdminData>("/api/admin/store"));
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 401) setSession({ authenticated: false });
      else setRefreshError("数据刷新失败，当前显示上次加载的内容，请重试。");
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
    }
  }, []);
  useEffect(() => {
    const label = navItems.find((item) => item.view === view)?.label ?? "后台";
    document.title = `${label} — 星河娱乐`;
  }, [view]);

  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    setSession({ authenticated: false });
    setData(null);
    toast.push("已退出运营后台", "info");
  }

  if (loading) return <main className="center-stage"><Spinner label="正在进入运营中枢" /></main>;
  if (!session?.authenticated || session.user?.role !== "admin") return <AdminLogin onSuccess={() => void load()} />;
  if (error || !data) return <main className="center-stage"><EmptyState title="后台连接失败" message={error || "无法读取运营数据。"} action={<Button onClick={() => void load()}><RefreshCw aria-hidden="true" />重试</Button>} /></main>;

  const currentLabel = navItems.find((item) => item.view === view)?.label ?? "仪表盘";
  return (
    <div className="admin-app">
      {mobileNav && <button className="admin-nav-backdrop" aria-label="关闭导航" onClick={() => setMobileNav(false)} />}
      <aside className={`admin-sidebar ${mobileNav ? "admin-sidebar--open" : ""}`} aria-label="后台导航">
        <div className="admin-sidebar__brand"><Brand /><button className="icon-button admin-sidebar__close" aria-label="关闭导航" onClick={() => setMobileNav(false)}><X aria-hidden="true" /></button></div>
        <nav>
          {[
            { label: "运营总览", views: ["dashboard", "members", "referrals"] },
            { label: "财务与支付管理", views: ["finance", "payments", "balance-grants", "rewards"] },
            { label: "接口管理", views: ["catalog", "games", "integration"] },
            { label: "风控管理", views: ["risk", "qa-permits"] },
            { label: "内容与服务", views: ["promotions", "support", "messages", "content"] },
          ].map(group => <div key={group.label}><p className="admin-nav-group">{group.label}</p>{group.views.map(key => {
            const { view: itemView, label, Icon } = navItems.find(item => item.view === key)!;
            return <a key={itemView} href={`/admin?view=${itemView}`} className={view === itemView ? "is-active" : ""} aria-current={view === itemView ? "page" : undefined} onClick={() => setMobileNav(false)}><Icon aria-hidden="true" /><span>{label}</span>{itemView === "finance" && data.stats.pendingTransactions > 0 && <small className="admin-nav-count" aria-label={`${data.stats.pendingTransactions} 笔待处理`}>{data.stats.pendingTransactions}</small>}</a>;
          })}</div>)}
        </nav>
        <div className="admin-sidebar__footer"><ShieldCheck aria-hidden="true" /><span><strong>星河运营后台</strong><small>配置与会员业务管理</small></span></div>
      </aside>
      <div className="admin-shell">
        <header className="admin-header">
          <button className="icon-button admin-menu" aria-label="打开导航" aria-expanded={mobileNav} onClick={() => setMobileNav(true)}><Menu aria-hidden="true" /></button>
          <div><span>运营中枢</span><h1>{currentLabel}</h1></div>
          <div className="admin-header__actions">
            <Button emphasis="ghost" intent="neutral" busy={refreshing} onClick={() => void refresh()}><RefreshCw aria-hidden="true" />刷新数据</Button>
            <a className="button button--neutral button--outline" href="/">查看前台</a>
            <button className="admin-user" onClick={() => void logout()}><span>A</span><span><strong>星河管理员</strong><small>退出登录</small></span><LogOut aria-hidden="true" /></button>
          </div>
        </header>
        <main className="admin-main">
          {refreshError && <div className="form-alert" role="alert">{refreshError}<Button emphasis="ghost" busy={refreshing} onClick={() => void refresh()}>重试</Button></div>}
          {view === "dashboard" && <Dashboard data={data} />}
          {view === "games" && <GamesView data={data} reload={refresh} />}
          {view === "promotions" && <AdminPromotions />}
          {view === "finance" && <><FinanceManagement onReviewed={refresh} /><AdminWallet /></>}
          {view === "referrals" && <AdminReferrals />}
          {view === "catalog" && <AdminCatalog />}
          {view === "payments" && <AdminPayments />}
          {view === "balance-grants" && <AdminBalanceGrants key={session.user.id} adminId={session.user.id} />}
          {view === "risk" && <RiskManagement />}
          {view === "qa-permits" && <AdminQaLoginPermits />}
          {view === "members" && <MembersView data={data} reload={refresh} />}
          {view === "content" && <ContentView site={data.site} reload={refresh} />}
          {view === "support" && <SupportManagement />}
          {view === "messages" && <MessageManagement users={data.users} />}
          {view === "integration" && <><AdminProviderDiagnostics /><IntegrationView data={data} reload={refresh} /></>}
          {view === "rewards" && <><AdminBetSync /><AdminRewards /></>}
        </main>
      </div>
    </div>
  );
}

function AdminLogin({ onSuccess }: { onSuccess: () => void }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const captcha = useCaptcha("login");
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  async function submit(event: FormEvent) {
    event.preventDefault(); if (lock.current) return;
    setError("");
    if (!username || !password) {
      setError("请输入管理员账号和密码。" );
      (!username ? usernameRef : passwordRef).current?.focus();
      return;
    }
    const security = captcha.submission(); if (!security) return;
    lock.current = true; setBusy(true);
    try {
      await api("/api/auth/login", { method: "POST", signal: AbortSignal.timeout(15000), body: JSON.stringify({ username, password, role: "admin", ...security }) });
      captcha.invalidate(); onSuccess();
    } catch (reason) {
      if (captcha.recover(reason)) return;
      setError(reason instanceof ApiError ? reason.message : "登录失败，请稍后重试。" );
    } finally {
      lock.current = false; setBusy(false);
    }
  }

  return (
    <main className="admin-login">
      <section className="admin-login__panel">
        <Brand />
        <span className="eyebrow"><Sparkles aria-hidden="true" /> OPERATIONS</span>
        <h1>欢迎回到<br />星河运营中枢</h1>
        <p>查看运营待办，管理游戏、活动与会员。</p>
        <div className="admin-login__orbit" aria-hidden="true">✦</div>
      </section>
      <section className="admin-login__form">
        <form className="form-stack" noValidate onSubmit={submit}>
          <div><span className="eyebrow">ADMIN SIGN IN</span><h2>管理员登录</h2><p>请输入管理员密码，登录运营后台。</p></div>
          {error && <div className="form-alert" role="alert"><Activity aria-hidden="true" />{error}</div>}
          <Field ref={usernameRef} label="管理员账号" autoComplete="username" disabled={busy} value={username} onChange={(event) => { setUsername(event.target.value); setError(""); }} />
          <div className="field">
            <label className="field__label" htmlFor="admin-password">密码</label>
            <span className="secret-field"><input ref={passwordRef} id="admin-password" className="input" type={showPassword ? "text" : "password"} autoComplete="current-password" disabled={busy} value={password} onChange={(event) => { setPassword(event.target.value); setError(""); }} /><button type="button" aria-label={showPassword ? "隐藏密码" : "显示密码"} aria-pressed={showPassword} onClick={() => setShowPassword((value) => !value)}>{showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}</button></span>
            <span className="field__message">客服管理和消息管理使用同一管理员账号。</span>
          </div>
          <CaptchaField captcha={captcha} disabled={busy} />
          <Button type="submit" busy={busy} disabled={!captcha.ready}>登录后台</Button>
          <a className="back-link" href="/">返回星河首页</a>
        </form>
      </section>
    </main>
  );
}

function Dashboard({ data }: { data: AdminData }) {
  const categoryCount = (category: Game["category"]) => data.games.filter((game) => game.category === category && game.enabled).length;
  const categoryTotal = (category: Game["category"]) => data.games.filter((game) => game.category === category).length;
  const maintenanceCount = data.games.filter((game) => game.enabled && game.maintenance).length;
  const recent = [...data.transactions].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 5);
  const cards = [
    { label: "会员总数", value: data.stats.users, note: `${data.stats.activeUsers} 位状态正常`, Icon: Users, tone: "blue" },
    { label: "待处理申请", value: data.stats.pendingTransactions, note: "充值与提现申请", Icon: BellRing, tone: "amber" },
    { label: "已上架内容", value: data.stats.enabledGames, note: "四大专区合计", Icon: Gamepad2, tone: "cyan" },
    { label: "净流入", value: money.format(data.stats.deposits - data.stats.withdrawals), note: "已批准申请合计", Icon: BarChart3, tone: "navy" },
  ];
  return (
    <div className="admin-page">
      <div className="page-title"><div><span className="eyebrow">运营工作台</span><h2>星河运营概览</h2><p>先处理待办，再查看游戏与会员的整体情况。</p></div></div>
      <section className="admin-workbench" aria-label="运营待办"><div><span className="eyebrow">待办提醒</span><h3>{data.stats.pendingTransactions ? `${data.stats.pendingTransactions} 笔资金申请等待处理` : "资金申请已全部处理"}</h3><p>{maintenanceCount} 个已上架游戏处于维护状态，请关注恢复进度。</p></div><div className="admin-workbench__actions"><a className="button button--brand button--solid" href="/admin?view=finance">处理资金申请<ChevronRight aria-hidden="true" /></a><a className="button button--neutral button--outline" href="/admin?view=games&status=maintenance">查看维护游戏</a></div></section>
      <div className="stat-grid">{cards.map(({ label, value, note, Icon, tone }) => <article className={`stat-card stat-card--${tone}`} key={label}><span className="stat-card__icon"><Icon aria-hidden="true" /></span><div><small>{label}</small><strong>{value}</strong><span>{note}</span></div></article>)}</div>
      <div className="dashboard-grid">
        <section className="admin-panel"><div className="panel-heading"><div><span className="eyebrow">CONTENT HEALTH</span><h3>四大专区状态</h3></div></div><div className="category-health">{([
          ["live", "真人视讯"], ["slots", "电子游艺"], ["sports", "体育电竞"], ["lottery", "彩票娱乐"],
        ] as const).map(([key, label]) => <div key={key}><span><strong>{label}</strong><small>{categoryCount(key)} / {categoryTotal(key)} 个已上架</small></span><div className="health-bar" aria-hidden="true"><i style={{ width: `${categoryTotal(key) ? categoryCount(key) / categoryTotal(key) * 100 : 0}%` }} /></div></div>)}</div></section>
        <section className="admin-panel"><div className="panel-heading"><div><span className="eyebrow">申请记录</span><h3>最近资金申请</h3></div><a className="back-link" href="/admin?view=finance">查看全部</a></div><div className="mini-list">{recent.map((item) => <article key={item.id}><span><strong>{item.username}</strong><small>{item.type === "deposit" ? "充值" : "提现"} · {dateTime.format(new Date(item.createdAt))}</small></span><span><strong>{money.format(item.amount)}</strong><StatusBadge tone={item.status === "approved" ? "success" : item.status === "rejected" ? "danger" : "warning"}>{item.status === "pending" ? "待处理" : item.status === "approved" ? "已批准" : "已驳回"}</StatusBadge></span></article>)}{!recent.length && <EmptyState title="暂无资金申请" message="会员提交申请后会显示在这里。" />}</div></section>
      </div>
      <nav className="admin-shortcuts" aria-label="常用管理入口">{([{view: "games", title: "游戏目录", text: "检查封面、上架与维护状态", Icon: Gamepad2}, {view: "promotions", title: "活动发布", text: "维护会员可见的礼遇内容", Icon: TicketCheck}, {view: "content", title: "首页内容", text: "更新主标题与公告", Icon: PencilLine}]).map(({view, title, text, Icon}) => <a href={`/admin?view=${view}`} key={view}><Icon aria-hidden="true" /><span><strong>{title}</strong><small>{text}</small></span><ChevronRight aria-hidden="true" /></a>)}</nav>
    </div>
  );
}

function GamesView({ data, reload }: { data: AdminData; reload: () => Promise<void> }) {
  const toast = useToast();
  const params = new URLSearchParams(window.location.search);
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [committed, setCommitted] = useState(params.get("q") ?? "");
  const [category, setCategory] = useState(params.get("category") ?? "all");
  const [statusFilter, setStatusFilter] = useState(params.get("status") ?? "all");
  const [page, setPage] = useState(Math.max(1, Number(params.get("page")) || 1));
  const [editing, setEditing] = useState<Game | null>(null);
  const [busyId, setBusyId] = useState("");
  const composing = useRef(false);

  useEffect(() => {
    if (composing.current || search === committed) return;
    const timer = window.setTimeout(() => {
      setCommitted(search);
      setPage(1);
      const next = new URLSearchParams(window.location.search);
      search ? next.set("q", search) : next.delete("q");
      next.set("page", "1");
      window.history.replaceState({}, "", `/admin?${next.toString()}`);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const filtered = useMemo(() => data.games.filter((game) =>
    `${game.name} ${game.code} ${game.providerCode ?? ""}`.toLowerCase().includes(committed.toLowerCase()) &&
    (category === "all" || game.category === category) &&
    (statusFilter === "all" || (statusFilter === "enabled" ? game.enabled : statusFilter === "disabled" ? !game.enabled : game.enabled && game.maintenance))
  ), [committed, data.games, category, statusFilter]);
  function updateFilter(key: "category" | "status", value: string) {
    if (key === "category") setCategory(value); else setStatusFilter(value);
    setPage(1);
    const next = new URLSearchParams(window.location.search);
    value === "all" ? next.delete(key) : next.set(key, value);
    next.set("page", "1");
    window.history.replaceState({}, "", `/admin?${next}`);
  }
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const rows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  function goPage(nextPage: number) {
    const clamped = Math.max(1, Math.min(totalPages, nextPage));
    setPage(clamped);
    const next = new URLSearchParams(window.location.search);
    next.set("page", String(clamped));
    window.history.replaceState({}, "", `/admin?${next.toString()}`);
  }

  async function patchGame(game: Game, body: Partial<Game>, message: string) {
    setBusyId(game.id);
    try {
      await api(`/api/admin/games/${game.id}`, { method: "PATCH", body: JSON.stringify(body) });
      await reload();
      toast.push(message);
    } catch (reason) {
      toast.push(reason instanceof ApiError ? reason.message : "操作失败，请重试。", "error");
    } finally { setBusyId(""); }
  }

  return (
    <div className="admin-page">
      <PageHeading eyebrow="GAME DIRECTORY" title="游戏管理" description="维护已导入游戏的名称、排序、上架与维护状态。" action={<a className="button button--brand button--solid" href="/admin?view=integration"><Plus aria-hidden="true" />新增游戏</a>} />
      <section className="admin-panel admin-panel--table">
        <div className="admin-filters">
          <SelectField label="游戏分类" value={category} onChange={(event) => updateFilter("category", event.target.value)}><option value="all">全部分类</option><option value="live">真人视讯</option><option value="slots">电子游艺</option><option value="sports">体育电竞</option><option value="lottery">彩票娱乐</option></SelectField>
          <SelectField label="上架与维护" value={statusFilter} onChange={(event) => updateFilter("status", event.target.value)}><option value="all">全部状态</option><option value="enabled">已上架</option><option value="disabled">已停用</option><option value="maintenance">已上架 · 维护中</option></SelectField>
          <a className="button button--neutral button--outline" href="/admin?view=integration">从供应商导入游戏</a>
        </div>
        <div className="table-toolbar">
          <label className="search-field"><span className="sr-only">搜索游戏</span><Search aria-hidden="true" /><input type="search" value={search} placeholder="搜索游戏名称或代码" onCompositionStart={() => { composing.current = true; }} onCompositionEnd={(event) => { composing.current = false; setSearch(event.currentTarget.value); }} onChange={(event) => setSearch(event.target.value)} />{search && <button aria-label="清除搜索" onClick={() => setSearch("")}><X aria-hidden="true" /></button>}</label>
          <span role="status">共 {filtered.length} 个游戏</span>
        </div>
        <div className="table-scroll">
          <table><caption className="sr-only">游戏列表</caption><thead><tr><th scope="col">游戏</th><th scope="col">分类</th><th scope="col">接口</th><th scope="col">排序</th><th scope="col">上架</th><th scope="col">运行状态</th><th scope="col">操作</th></tr></thead><tbody>
{rows.map((game) => <tr key={game.id}><td><div className="admin-game-identity">{game.image ? <img src={game.image} alt="" width="72" height="45" loading="lazy" /> : <span className="admin-game-placeholder"><Gamepad2 aria-hidden="true" /></span>}<span><strong>{game.name}</strong><small className="table-subline">{game.code}</small></span></div></td><td>{categoryLabel(game.category)}</td><td><strong>{game.providerCode ?? "本地"}</strong><small className="table-subline">{game.providerCode ? `${game.providerGameType}:${game.providerGameCode}` : "未绑定供应商"}</small></td><td className="numeric">{game.sort}</td><td><StatusBadge tone={game.enabled ? "success" : "neutral"}>{game.enabled ? "已上架" : "已停用"}</StatusBadge></td><td><StatusBadge tone={game.maintenance ? "warning" : "success"}>{game.maintenance ? "维护中" : "正常"}</StatusBadge></td><td><div className="table-actions"><Button emphasis="ghost" intent="neutral" disabled={Boolean(busyId)} onClick={() => setEditing(game)}><PencilLine aria-hidden="true" />编辑</Button><Button emphasis="ghost" intent="neutral" busy={busyId === game.id} onClick={() => void patchGame(game, { maintenance: !game.maintenance }, game.maintenance ? "已结束维护" : "已进入维护")}>{game.maintenance ? "结束维护" : "设为维护"}</Button><Button emphasis="outline" intent={game.enabled ? "warning" : "success"} disabled={busyId === game.id} onClick={() => void patchGame(game, { enabled: !game.enabled }, game.enabled ? "游戏已停用，前台将不再显示" : "游戏已上架并同步到前台")}>{game.enabled ? "停用" : "上架"}</Button></div></td></tr>)}
          </tbody></table>
          {!rows.length && <EmptyState title="没有匹配的游戏" message="请清除搜索条件或新增游戏。" action={search ? <Button emphasis="outline" onClick={() => setSearch("")}>清除搜索</Button> : undefined} />}
        </div>
        <div className="pagination" aria-label="游戏列表分页"><span>第 {(safePage - 1) * pageSize + (rows.length ? 1 : 0)}–{(safePage - 1) * pageSize + rows.length} 项，共 {filtered.length} 项</span><div><Button emphasis="ghost" intent="neutral" disabled={safePage === 1} aria-label="上一页" onClick={() => goPage(safePage - 1)}><ChevronLeft aria-hidden="true" /></Button><span aria-current="page">{safePage} / {totalPages}</span><Button emphasis="ghost" intent="neutral" disabled={safePage === totalPages} aria-label="下一页" onClick={() => goPage(safePage + 1)}><ChevronRight aria-hidden="true" /></Button></div></div>
      </section>
      {editing && <EditGameModal game={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await reload(); toast.push("游戏资料已保存"); }} />}
    </div>
  );
}

function EditGameModal({ game, onClose, onSaved }: { game: Game; onClose: () => void; onSaved: () => Promise<void> }) {
  const [name, setName] = useState(game.name);
  const [description, setDescription] = useState(game.description);
  const [sort, setSort] = useState(String(game.sort));
  const [featured, setFeatured] = useState(game.featured);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (!name.trim() || !Number.isInteger(Number(sort)) || Number(sort) < 1) {
      setError("请填写游戏名称，排序须为大于零的整数。"); nameRef.current?.focus(); return;
    }
    setBusy(true); setError("");
    try {
      await api(`/api/admin/games/${game.id}`, { method: "PATCH", body: JSON.stringify({ name: name.trim(), description, sort: Number(sort), featured }) });
      await onSaved();
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : "保存失败，请重试。"); }
    finally { setBusy(false); }
  }
  return <Modal open onClose={onClose} preventClose={busy} title="编辑游戏" description={`${game.code} · 保存后更新前台展示内容。`}><form className="form-stack" noValidate onSubmit={save}>{error && <div className="form-alert" role="alert">{error}</div>}<Field ref={nameRef} label="游戏名称" value={name} maxLength={40} onChange={(event) => setName(event.target.value)} /><TextAreaField label="游戏说明" rows={3} maxLength={120} value={description} onChange={(event) => setDescription(event.target.value)} /><Field label="排序" type="number" min="1" step="1" value={sort} onChange={(event) => setSort(event.target.value)} hint="数字越小，展示越靠前。" /><label><input type="checkbox" checked={featured} onChange={(event) => setFeatured(event.target.checked)} /> 显示在首页精选</label><div className="modal-actions"><Button type="button" emphasis="outline" intent="neutral" onClick={onClose}>取消</Button><Button type="submit" busy={busy}>保存更改</Button></div></form></Modal>;
}

function MembersView({ data, reload }: { data: AdminData; reload: () => Promise<void> }) {
  const toast = useToast(); const [target, setTarget] = useState<User | null>(null); const [busy, setBusy] = useState(false);
  async function toggle() { if (!target) return; setBusy(true); const status = target.status === "active" ? "locked" : "active"; try { await api(`/api/admin/users/${target.id}`, { method: "PATCH", body: JSON.stringify({ status }) }); await reload(); toast.push(status === "active" ? "会员已恢复" : "会员已锁定"); setTarget(null); } catch (reason) { toast.push(reason instanceof ApiError ? reason.message : "操作失败。", "error"); } finally { setBusy(false); } }
  return <div className="admin-page"><PageHeading eyebrow="MEMBERS" title="会员管理" description="查看体验会员余额与账号状态。" /><section className="admin-panel admin-panel--table"><div className="table-scroll"><table><caption className="sr-only">会员列表</caption><thead><tr><th scope="col">会员</th><th scope="col">余额</th><th scope="col">状态</th><th scope="col">注册时间</th><th scope="col">操作</th></tr></thead><tbody>{data.users.map((user) => <tr key={user.id}><td><strong>{user.displayName}</strong><small className="table-subline">{user.username}</small></td><td className="numeric">{money.format(user.balance)}</td><td><StatusBadge tone={user.status === "active" ? "success" : "danger"}>{user.status === "active" ? "正常" : "已锁定"}</StatusBadge></td><td>{dateTime.format(new Date(user.createdAt))}</td><td><Button emphasis="outline" intent={user.status === "active" ? "warning" : "success"} onClick={() => setTarget(user)}>{user.status === "active" ? "锁定会员" : "恢复会员"}</Button></td></tr>)}</tbody></table></div></section><Modal open={Boolean(target)} onClose={() => setTarget(null)} preventClose={busy} title={target?.status === "active" ? "锁定该会员？" : "恢复该会员？"} description={target?.displayName}><p className="confirm-copy">{target?.status === "active" ? "锁定后该会员不能登录，但历史申请和余额会保留。" : "恢复后该会员可以再次登录前台。"}</p><div className="modal-actions"><Button intent="neutral" emphasis="outline" onClick={() => setTarget(null)} data-autofocus>取消</Button><Button intent={target?.status === "active" ? "warning" : "success"} busy={busy} onClick={() => void toggle()}>{target?.status === "active" ? "锁定会员" : "恢复会员"}</Button></div></Modal></div>;
}

function ContentView({ site, reload }: { site: SiteSettings; reload: () => Promise<void> }) {
  const toast = useToast();
  const [form, setForm] = useState(site);
  const [error, setError] = useState("");
  const [errorField, setErrorField] = useState("");
  const [busy, setBusy] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const onlineServiceRef = useRef<HTMLInputElement>(null);
  const telegramServiceRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (busy) return;
    if (errorField === "onlineServiceUrl") onlineServiceRef.current?.focus();
    if (errorField === "telegramServiceUrl") telegramServiceRef.current?.focus();
  }, [errorField, busy]);
  function updateService(field: "onlineServiceUrl" | "telegramServiceUrl", value: string) {
    setForm((current) => ({ ...current, [field]: value }));
    if (errorField === field) { setError(""); setErrorField(""); }
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(""); setErrorField("");
    if (!form.heroTitle.trim() || !form.heroSubtitle.trim()) {
      setError("主标题和副标题不能为空。");
      if (!form.heroTitle.trim()) titleRef.current?.focus();
      else document.getElementById("site-hero-subtitle")?.focus();
      return;
    }
    setBusy(true);
    try {
      const saved = await api<SiteSettings>("/api/admin/site", { method: "PUT", body: JSON.stringify(form) });
      setForm(saved);
      await reload();
      toast.push("首页内容与客服设置已保存并同步到前台");
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "保存失败，请重试。");
      if (reason instanceof ApiError && reason.field) {
        setErrorField(reason.field);
      }
    } finally { setBusy(false); }
  }
  return <div className="admin-page">
    <PageHeading eyebrow="SITE CONTENT" title="内容管理" description="维护首页文字、公告与客服联系方式。" />
    <section className="admin-panel admin-form-panel"><form className="form-stack" noValidate onSubmit={submit}>
      {error && <div className="form-alert" role="alert"><Activity aria-hidden="true" />{error}</div>}
      <Field label="品牌名称" value={form.name} disabled hint="品牌名称固定为“星河娱乐”，防止误改。" />
      <Field ref={titleRef} label="首页主标题" disabled={busy} value={form.heroTitle} onChange={(event) => setForm({ ...form, heroTitle: event.target.value })} />
      <TextAreaField id="site-hero-subtitle" label="首页副标题" disabled={busy} rows={3} value={form.heroSubtitle} onChange={(event) => setForm({ ...form, heroSubtitle: event.target.value })} />
      <TextAreaField label="顶部公告" disabled={busy} rows={3} value={form.announcement} onChange={(event) => setForm({ ...form, announcement: event.target.value })} />
      <Field label="客服入口名称" disabled={busy} value={form.serviceLabel} onChange={(event) => setForm({ ...form, serviceLabel: event.target.value })} />
      <SelectField label="在线客服方式" disabled={busy} value={form.onlineServiceMode ?? "builtin"} onChange={event => setForm({ ...form, onlineServiceMode: event.target.value as SiteSettings["onlineServiceMode"] })}><option value="builtin">本站在线客服</option><option value="external">外部客服链接</option><option value="disabled">暂时关闭</option></SelectField>
      <Field ref={onlineServiceRef} label="在线客服地址" disabled={busy} type="url" autoComplete="off" autoCapitalize="none" spellCheck={false} maxLength={2048} value={form.onlineServiceUrl ?? ""} onChange={(event) => updateService("onlineServiceUrl", event.target.value)} error={errorField === "onlineServiceUrl" ? error : undefined} hint="仅选择外部客服链接时使用；本站在线客服无需填写。" placeholder="https://" />
      <Field ref={telegramServiceRef} label="Telegram 客服地址" disabled={busy} autoComplete="off" autoCapitalize="none" spellCheck={false} maxLength={128} value={form.telegramServiceUrl ?? ""} onChange={(event) => updateService("telegramServiceUrl", event.target.value)} error={errorField === "telegramServiceUrl" ? error : undefined} hint="填写 @用户名 或 https://t.me/用户名；留空时显示暂未开放。" placeholder="@用户名" />
      <div className="sticky-actions"><Button type="submit" busy={busy}><PencilLine aria-hidden="true" />保存更改</Button></div>
    </form></section>
  </div>;
}

type IntegrationStatus = { configured: boolean; transfersEnabled: boolean; providers: string[] };
type ProviderCatalogGame = { code: string; gameCode: string; gameType: string; img?: string; name: string };

function IntegrationView({ data, reload }: { data: AdminData; reload: () => Promise<void> }) {
  const toast = useToast();
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [checks, setChecks] = useState<Record<string, { count?: number; error?: string }>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [catalogProvider, setCatalogProvider] = useState("");
  const [catalogGames, setCatalogGames] = useState<ProviderCatalogGame[]>([]);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [visibleCount, setVisibleCount] = useState(20);
  const [importingGameCode, setImportingGameCode] = useState("");
  const catalogRequest = useRef<AbortController | null>(null);
  const catalogSearchRef = useRef<HTMLInputElement>(null);

  const loadStatus = useCallback(async () => {
    setError("");
    try {
      setStatus(await api<IntegrationStatus>("/api/admin/integrations/yibo/status"));
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "无法读取接口状态。");
    }
  }, []);

  useEffect(() => { void loadStatus(); }, [loadStatus]);
  useEffect(() => () => catalogRequest.current?.abort(), []);

  async function checkProviders() {
    if (!status?.configured) return;
    setBusy(true);
    setError("");
    const next: Record<string, { count?: number; error?: string }> = {};
    await Promise.all(status.providers.map(async (provider) => {
      try {
        const result = await api<{ games: unknown[] }>(`/api/admin/integrations/yibo/catalog/${provider}`);
        next[provider] = { count: result.games.length };
      } catch (reason) {
        next[provider] = { error: reason instanceof ApiError ? reason.message : "连接失败" };
      }
    }));
    setChecks(next);
    setBusy(false);
    toast.push(Object.values(next).every((item) => !item.error) ? "所有已配置供应商均已完成检测" : "检测完成，部分供应商返回异常", Object.values(next).some((item) => item.error) ? "info" : "success");
  }

  const closeCatalog = useCallback(() => {
    catalogRequest.current?.abort();
    setCatalogProvider("");
    setCatalogGames([]);
    setCatalogSearch("");
    setCatalogError("");
    setVisibleCount(20);
  }, []);

  async function openCatalog(provider: string) {
    catalogRequest.current?.abort();
    const controller = new AbortController();
    catalogRequest.current = controller;
    setCatalogProvider(provider);
    setCatalogGames([]);
    setCatalogSearch("");
    setCatalogError("");
    setVisibleCount(20);
    setCatalogLoading(true);
    try {
      const result = await api<{ games: ProviderCatalogGame[] }>(`/api/admin/integrations/yibo/catalog/${provider}`, { signal: controller.signal });
      if (catalogRequest.current !== controller || controller.signal.aborted) return;
      setCatalogGames(result.games);
      setChecks((current) => ({ ...current, [provider]: { count: result.games.length } }));
    } catch (reason) {
      if (catalogRequest.current !== controller || controller.signal.aborted || (reason instanceof DOMException && reason.name === "AbortError")) return;
      setCatalogError(reason instanceof ApiError ? reason.message : "目录读取失败，请重试。");
    } finally {
      if (catalogRequest.current === controller) setCatalogLoading(false);
    }
  }

  async function importGame(game: ProviderCatalogGame) {
    setImportingGameCode(`${game.gameType}:${game.gameCode}`);
    setCatalogError("");
    try {
      await api<Game>("/api/admin/integrations/yibo/import", {
        method: "POST",
        body: JSON.stringify({ provider: catalogProvider, gameType: game.gameType, gameCode: game.gameCode }),
      });
      await reload();
      toast.push(`${game.name} 已导入游戏管理并上架`);
    } catch (reason) {
      setCatalogError(reason instanceof ApiError ? reason.message : "导入失败，请重试。");
    } finally {
      setImportingGameCode("");
    }
  }

  const normalizedCatalogSearch = catalogSearch.trim().toLowerCase();
  const filteredCatalog = catalogGames.filter((game) => `${game.name} ${game.gameCode} ${game.gameType}`.toLowerCase().includes(normalizedCatalogSearch));
  const visibleCatalog = filteredCatalog.slice(0, visibleCount);
  const importedCatalogKeys = new Set(data.games.map((game) => `${game.providerCode}:${game.providerGameType}:${game.providerGameCode}`));

  return (
    <div className="admin-page">
      <PageHeading eyebrow="GAME SERVICE" title="接口中心" description="监控第三方游戏目录连接；商户账号与 KEY 始终保留在服务端。" action={<Button busy={busy} disabled={!status?.configured} onClick={() => void checkProviders()}><RefreshCw aria-hidden="true" />检测连接</Button>} />
      <section className="admin-panel"><div className="panel-heading"><h3>如何添加供应商游戏</h3></div><div className="ops-note"><Gamepad2 aria-hidden="true" /><div><p>选择供应商 → 查看目录 → 搜索游戏 → 导入并上架。导入会在本站创建游戏入口，玩家点击后进入供应商提供的游戏。</p><p>导入后可在“游戏管理”调整名称、说明、排序和首页精选，也可以停用。目录读取成功不代表商户已开通全部游戏；能否进入还取决于供应商权限与维护状态。导入不会充值或转移额度。</p></div></div></section>
      {error && <div className="form-alert" role="alert"><Activity aria-hidden="true" />{error}</div>}
      <div className="integration-summary">
        <article className="admin-panel"><span className="integration-summary__icon"><Activity aria-hidden="true" /></span><div><small>YGAPI 状态</small><strong>{status?.configured ? "已安全配置" : "等待配置"}</strong><p>前端只接收是否可用，不会收到账号或密钥。</p></div></article>
        <article className="admin-panel"><span className="integration-summary__icon"><ShieldCheck aria-hidden="true" /></span><div><small>额度转换</small><strong>{status?.transfersEnabled ? "已启用" : "默认关闭"}</strong><p>开启后会员可在钱包确认转账；每笔订单先持久保存，按原单核对后结算。</p></div></article>
      </div>
      <section className="admin-panel admin-panel--table">
        <div className="panel-heading"><div><span className="eyebrow">PROVIDER DIRECTORY</span><h3>已配置供应商</h3></div><span role="status">{status?.providers.length ?? 0} 个接口标识</span></div>
        <div className="table-scroll">
          <table><caption className="sr-only">第三方游戏供应商连接状态</caption><thead><tr><th scope="col">供应商</th><th scope="col">用途</th><th scope="col">目录结果</th><th scope="col">连接状态</th><th scope="col">操作</th></tr></thead><tbody>
            {(status?.providers ?? []).map((provider) => {
              const result = checks[provider];
              return <tr key={provider}><td><strong>{provider}</strong></td><td>{provider === "AG" ? "真人视讯" : provider === "CQ9" ? "电子游艺" : provider === "SBO" ? "体育电竞" : provider === "TCG" ? "彩票娱乐" : "扩展内容"}</td><td>{result?.count === undefined ? "尚未检测" : `${result.count} 个目录项目`}</td><td><StatusBadge tone={result?.error ? "danger" : result ? "success" : "neutral"}>{result?.error ?? (result ? "连接正常" : "待检测")}</StatusBadge></td><td><Button emphasis="outline" intent="neutral" onClick={() => void openCatalog(provider)}>查看目录</Button></td></tr>;
            })}
          </tbody></table>
          {!status?.providers.length && <EmptyState title="未配置供应商" message="请在服务端环境变量中设置 YIBO_API_CODES。" />}
        </div>
      </section>
      <Modal open={Boolean(catalogProvider)} onClose={closeCatalog} preventClose={Boolean(importingGameCode)} title={`${catalogProvider} 游戏目录`} description="名称、类型和启动代码均由服务端从供应商目录复核；导入后会进入游戏管理并默认上架。">
        <div className="catalog-browser">
          <div className="catalog-browser__toolbar">
            <label className="search-field"><span className="sr-only">搜索供应商目录</span><Search aria-hidden="true" /><input ref={catalogSearchRef} type="search" value={catalogSearch} placeholder="搜索名称、代码或类型" onChange={(event) => { setCatalogSearch(event.target.value); setVisibleCount(20); }} />{catalogSearch && <button type="button" aria-label="清除目录搜索" onClick={() => { setCatalogSearch(""); setVisibleCount(20); catalogSearchRef.current?.focus(); }}><X aria-hidden="true" /></button>}</label>
            <span role="status">显示 {Math.min(visibleCount, filteredCatalog.length)} / {filteredCatalog.length} 项</span>
          </div>
          {catalogError && <div className="form-alert" role="alert"><Activity aria-hidden="true" />{catalogError}</div>}
          {catalogLoading ? <div className="catalog-browser__state"><Spinner label="正在读取供应商目录" /></div> : visibleCatalog.length ? <div className="catalog-list" aria-label={`${catalogProvider} 目录项目`}>
            {visibleCatalog.map((game) => {
              const imported = importedCatalogKeys.has(`${game.code}:${game.gameType}:${game.gameCode}`);
              const supported = Boolean(categoryForProviderType(game.gameType));
              return <article className="catalog-item" key={`${game.code}:${game.gameType}:${game.gameCode}`}><div><strong>{game.name || `${game.code} 游戏`}</strong><span><code>{game.gameCode}</code><small>类型 {game.gameType} · {categoryForProviderType(game.gameType) ?? "暂未开放"}</small></span></div>{imported ? <StatusBadge tone="success">已导入</StatusBadge> : supported ? <Button busy={importingGameCode === `${game.gameType}:${game.gameCode}`} disabled={Boolean(importingGameCode)} onClick={() => void importGame(game)}><Plus aria-hidden="true" />导入并上架</Button> : <StatusBadge tone="neutral">暂未开放</StatusBadge>}</article>;
            })}
          </div> : <EmptyState title={catalogGames.length ? "没有匹配的目录项目" : "目录暂无项目"} message={catalogGames.length ? "请更换关键词或清除搜索。" : "该供应商当前没有返回可导入项目。"} action={catalogSearch ? <Button emphasis="outline" onClick={() => setCatalogSearch("")}>清除搜索</Button> : catalogError ? <Button emphasis="outline" onClick={() => void openCatalog(catalogProvider)}>重试</Button> : undefined} />}
          {!catalogLoading && visibleCount < filteredCatalog.length && <div className="catalog-browser__more"><Button emphasis="outline" intent="neutral" onClick={() => setVisibleCount((count) => count + 20)}>加载更多</Button></div>}
        </div>
      </Modal>
    </div>
  );
}

function PageHeading({ eyebrow, title, description, action }: { eyebrow: string; title: string; description: string; action?: ReactNode }) {
  return <div className="page-title"><div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2><p>{description}</p></div>{action}</div>;
}

function categoryLabel(category: Game["category"]) {
  return { live: "真人视讯", slots: "电子游艺", sports: "体育电竞", lottery: "彩票娱乐" }[category];
}

function categoryForProviderType(gameType: string) {
  const labels: Record<string, string> = { "1": "真人视讯", "3": "电子游艺", "4": "彩票娱乐", "5": "体育竞技", "7": "电子竞技" };
  return labels[gameType];
}
